import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config/ApiConfig';
import Navbar from './components/Navbar';
import Pagination from './components/Pagination';
import PageCard from './components/PageCard';
import useDefaultModal from './components/hooks/useDefaultModal';
import useConfirmModal from './components/hooks/useConfirmModal';
import ConfirmModal from './components/modals/ConfirmModal';
import DefaultModal from './components/modals/DefaultModal';
import usePagination from './components/hooks/usePagination';
import Footer from './components/Footer';

// Certifique-se de que o CSS para .full-screen-div ainda está sendo importado
// .full-screen-div { display: flex; flex-direction: column; min-height: 100vh; }

const UploadTemplate = () => {
    // ... todo o seu state, useEffects e funções (NÃO MUDA NADA AQUI) ...
    const [file, setFile] = useState(null);
    const [nome, setNome] = useState('');
    const [templates, setTemplates] = useState([]);
    const navigate = useNavigate();
    const token = localStorage.getItem('token');
    const fileInputRef = useRef(null);
    const { setDefaultModal, showDefaultModal } = useDefaultModal();
    const { setConfirmModal, showConfirmModal } = useConfirmModal();
    const totalItems = templates.length;
    const { setPagination } = usePagination(totalItems);
    const selectedDemandas = templates.slice(
        setPagination.startIndex,
        setPagination.startIndex + setPagination.rowsPerPage
    );

    useEffect(() => {
        if (!token) {
            navigate('/');
        } else {
            fetchTemplates();
        }
    }, [navigate, token]);

    const fetchTemplates = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL.TEMPLATE}/list`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            // Assumindo que a resposta do backend inclui o nome original do arquivo
            setTemplates(response.data);
        } catch (error) {
            console.error('Erro ao buscar os templates:', error);
        }
    };

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile && selectedFile.type !== "application/vnd.openxmlformats-officedocument.wordprocessingml.document") {
            e.target.value = "";
            setFile(null);
            showDefaultModal("error", "Por favor, selecione um arquivo .docx");
        } else {
            setFile(selectedFile);
        }
    };

    const handleNomeChange = (e) => setNome(e.target.value);

    const handleUpload = async (e) => {
        e.preventDefault();

        if (!file || !nome) {
            showDefaultModal('error', 'Por favor, forneça um nome e selecione um arquivo.');
            return;
        }

        const formData = new FormData();
        formData.append('file', file);
        formData.append('nome', nome);

        try {
            await axios.post(`${API_BASE_URL.TEMPLATE}/save`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    Authorization: `Bearer ${token}`
                },
            });
            setNome('');
            setFile(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
            fetchTemplates();
            showDefaultModal("success", "Template salvo com sucesso!");
        } catch (error) {
            console.error('Erro ao enviar o template:', error);
            showDefaultModal('error', 'Erro ao enviar o template.');
        }
    };

    const handleDeleteClick = (templateToDelete) => {
        showConfirmModal('delete', () => handleDeleteConfirm(templateToDelete));
    };

    const handleDeleteConfirm = async (templateToDelete) => {
        if (templateToDelete) {
            try {
                await axios.delete(`${API_BASE_URL.TEMPLATE}/delete/${templateToDelete.id}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                showDefaultModal('success', 'Template excluído com sucesso.');
                fetchTemplates();
            } catch (error) {
                console.error('Erro ao excluir o template:', error);
                showDefaultModal('error', 'Erro ao excluir o template.');
            }
        }
    };

    const handleDownload = async (template) => {
        try {
            const response = await axios.get(`${API_BASE_URL.TEMPLATE}/download/${template.id}`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob', // Importante para tratar a resposta como um arquivo
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));

            const link = document.createElement('a');
            link.href = url;

            const contentDisposition = response.headers['content-disposition'];
            let fileName = `${template.nome}.docx`; // Nome padrão
            if (contentDisposition) {
                const fileNameMatch = contentDisposition.match(/filename="?(.+)"?/);
                if (fileNameMatch.length === 2)
                    fileName = fileNameMatch[1];
            }

            link.setAttribute('download', fileName);

            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url); // Libera a memória

        } catch (error) {
            console.error('Erro ao baixar o template:', error);
            showDefaultModal('error', 'Não foi possível fazer o download do template.');
        }
    };

    return (
        <div className='full-screen-div'>
            <Navbar />

            {/* 1. O wrapper para o background */}
            <div
                style={{
                    flex: '1 0 auto',
                    backgroundImage: `url('/assets/desktop.png')`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundAttachment: 'fixed',
                    overflowY: 'auto'
                }}
            >
                {/* 2. O container original do conteúdo, com seu espaçamento superior */}
                <div className="container mt-4 pt-5">
                    <PageCard title="Upload de Template" icon="bi-upload" embedded
                        className="p-4 mb-0" // Adicionado padding e mantido margin-bottom
                        style={{ backgroundColor: 'rgba(255, 255, 255, 0.9)' }} // Adicionado fundo semi-transparente
                    >
                        <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center">
                            <h4 className="text-secondary text-shadow">CADASTRO DE TEMPLATE</h4>
                        </div>
                        {/* Formulário e Tabela agora vivem dentro do mesmo card */}
                        <div className='row g-3'>
                            <div className="col-md-6">
                                <form onSubmit={handleUpload}>
                                    <div className='mb-0 input-group-sm'>
                                        <label className='form-label'>Nome do Template</label>
                                        <select onChange={handleNomeChange} value={nome} name='nome' className='form-select'>
                                            <option value="">Selecione...</option>
                                            <option value="Ata">Ata</option>
                                            <option value="Edital">Edital</option>
                                        </select>
                                    </div>
                                    <div className='mb-3 input-group-sm'>
                                        <label className='form-label'>Selecione o Arquivo</label>
                                        <input
                                            className='form-control'
                                            type="file"
                                            onChange={handleFileChange}
                                            name="file"
                                            ref={fileInputRef}
                                            accept=".docx"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <Link className="btn btn-secondary btn-sm me-2" to="/home">
                                            <i className="bi bi-house" /> Voltar
                                        </Link>
                                        <button className='btn btn-success btn-sm' type="submit">Enviar</button>
                                    </div>
                                </form>
                            </div>
                        </div>
                        <div className="mt-3">
                            <h5 className="mb-2">Templates Salvos</h5>
                            <div className="table-responsive shadow-sm mb-2">
                                <table className="table table-striped table-bordered table-hover small">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Nome do Template</th>
                                            <th>Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedDemandas.length > 0 ? (
                                            selectedDemandas.map((template, index) => (
                                                <tr key={index}>
                                                    <td>{template.nome}</td>
                                                    <td>
                                                        <button
                                                            className="btn btn-primary btn-sm me-2"
                                                            onClick={() => handleDownload(template)}
                                                        >
                                                            <i className="bi bi-download me-1" /> Baixar
                                                        </button>
                                                        <button
                                                            className="btn btn-danger btn-sm"
                                                            onClick={() => handleDeleteClick(template)}
                                                        >
                                                            <i className="bi bi-trash3 me-1" /> Excluir
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="2" className="text-center">Nenhum template encontrado.</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <Pagination {...setPagination} />

                        <ConfirmModal {...setConfirmModal} />
                        <DefaultModal {...setDefaultModal} />
                    </PageCard>
                </div>
            </div>
            <Footer />
        </div>
    );
};

export default UploadTemplate;